import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Grid,
} from '@mui/material'

interface LocationUpdatePayloadProps {
  payload: Record<string, any>
}

const formatValue = (value: any, unit?: string): string => {
  if (value === undefined || value === null) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (unit) return `${value} ${unit}`
  return String(value)
}

interface SectionRow {
  label: string
  value: any
  unit?: string
}

interface SectionProps {
  title: string
  rows: SectionRow[]
}

const Section = ({ title, rows }: SectionProps) => {
  const visibleRows = rows.filter(
    (r) => r.value !== undefined && r.value !== null
  )
  if (visibleRows.length === 0) return null

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.8rem' }}
      >
        {title}
      </Typography>
      <Table size="small" sx={{ '& td, & th': { py: 0.25, px: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: '50%' }}>
              Parameter
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row) => (
            <TableRow key={row.label}>
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {row.label}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                {formatValue(row.value, row.unit)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

const LocationUpdatePayload = ({ payload }: LocationUpdatePayloadProps) => {
  const data = payload?.data || payload

  const sections: SectionProps[] = [
    {
      title: 'Event Metadata',
      rows: [
        { label: 'Event Type', value: payload?.eventType },
        { label: 'Source', value: payload?.source },
        { label: 'Timestamp', value: payload?.timestamp },
        { label: 'Contract ID', value: payload?.contractId },
      ],
    },
    {
      title: 'Vehicle Identity',
      rows: [
        { label: 'ID', value: data?.Id },
        { label: 'VIN', value: data?.vin },
        { label: 'Trip ID', value: data?.vehicle?.tripId },
        { label: 'Device ID', value: data?.vehicle?.deviceId },
      ],
    },
    {
      title: 'Position & Geolocation',
      rows: [
        { label: 'Latitude', value: data?.latitude, unit: 'deg' },
        { label: 'Longitude', value: data?.longitude, unit: 'deg' },
        { label: 'Heading', value: data?.heading, unit: 'deg' },
        { label: 'GPS Source', value: data?.geolocation?.source },
        { label: 'GPS Latitude', value: data?.geolocation?.latitude, unit: 'deg' },
        { label: 'GPS Longitude', value: data?.geolocation?.longitude, unit: 'deg' },
        { label: 'Altitude', value: data?.geolocation?.altitude?.value, unit: 'm' },
        { label: 'GPS Accuracy', value: data?.geolocation?.gps?.accuracy?.value, unit: 'm' },
      ],
    },
    {
      title: 'Speed & Motion',
      rows: [
        { label: 'Speed', value: data?.speed?.value, unit: 'km/h' },
        { label: 'GPS Speed', value: data?.gpsSpeed?.value, unit: 'km/h' },
        { label: 'Acceleration', value: data?.acceleration?.value, unit: 'm/s2' },
        { label: 'Lateral Acceleration', value: data?.accelerationLat?.value, unit: 'm/s2' },
        { label: 'Vertical Acceleration', value: data?.accelerationVert?.value, unit: 'm/s2' },
        { label: 'Instant Acceleration', value: data?.accelerationInst?.value, unit: 'm/s2' },
        { label: 'Instant Lateral Accel.', value: data?.accelerationInstLat?.value, unit: 'm/s2' },
        { label: 'Instant Vertical Accel.', value: data?.accelerationInstVert?.value, unit: 'm/s2' },
      ],
    },
    {
      title: 'Rotation & Orientation',
      rows: [
        { label: 'Lateral Ang. X', value: data?.rotation?.latAngX?.value, unit: 'deg/s' },
        { label: 'Long. Ang. Y', value: data?.rotation?.longAngY?.value, unit: 'deg/s' },
        { label: 'Vertical Ang. Z', value: data?.rotation?.vertAngZ?.value, unit: 'deg/s' },
        { label: 'Matrix Axx', value: data?.rotation?.matrixAxx },
        { label: 'Matrix Axy', value: data?.rotation?.matrixAxy },
        { label: 'Matrix Axz', value: data?.rotation?.matrixAxz },
        { label: 'Matrix Ayx', value: data?.rotation?.matrixAyx },
        { label: 'Matrix Ayy', value: data?.rotation?.matrixAyy },
        { label: 'Matrix Ayz', value: data?.rotation?.matrixAyz },
        { label: 'Matrix Azx', value: data?.rotation?.matrixAzx },
        { label: 'Matrix Azy', value: data?.rotation?.matrixAzy },
        { label: 'Matrix Azz', value: data?.rotation?.matrixAzz },
      ],
    },
    {
      title: 'Engine Telemetry',
      rows: [
        { label: 'Engine Speed', value: data?.engine?.speed?.value, unit: 'rpm' },
        { label: 'Contact', value: data?.engine?.contact },
        { label: 'Ignition', value: data?.engine?.ignition },
      ],
    },
    {
      title: 'Fuel Telemetry',
      rows: [
        { label: 'Fuel Level', value: data?.fuel?.level?.value, unit: 'L' },
        { label: 'Residual Autonomy', value: data?.fuel?.residualAutonomy?.value, unit: 'km' },
        { label: 'Instant Consumption', value: data?.fuel?.instantConsumption?.value, unit: 'L/100km' },
      ],
    },
    {
      title: 'Odometer & Maintenance',
      rows: [
        { label: 'Odometer', value: data?.odometer?.value, unit: 'km' },
        { label: 'Maintenance Odometer', value: data?.maintenance?.odometer?.value, unit: 'km' },
      ],
    },
    {
      title: 'Transmission',
      rows: [
        { label: 'Gear State', value: data?.transmissionGear?.state },
      ],
    },
    {
      title: 'ADAS & Safety',
      rows: [
        { label: 'ABS', value: data?.adas?.abs },
        { label: 'CRC Airbag', value: data?.adas?.crcAirbag },
        { label: 'Accel Pedal Pos', value: data?.crash?.accelPedalPos },
        { label: 'Steering Wheel Angle', value: data?.crash?.steerWhlAngle?.value, unit: 'deg' },
        { label: 'Airbag Deploy Status', value: data?.crash?.airbagDeploySt },
      ],
    },
    {
      title: 'Device / Configuration',
      rows: [
        { label: 'Privacy', value: data?.setup?.privacy },
        { label: 'Connect Status', value: data?.setup?.connectSts },
        { label: 'Device Calibration', value: data?.setup?.deviceCalib },
      ],
    },
  ]

  return (
    <Grid container spacing={2}>
      {sections.map((section) => (
        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={section.title}>
          <Section title={section.title} rows={section.rows} />
        </Grid>
      ))}
    </Grid>
  )
}

export default LocationUpdatePayload
